import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Link,
  Loader2,
  RefreshCw,
  Unlink,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import type {
  FilevineOrganizationWithLinkStatusSchema,
  FilevineProjectConfigWithSectionsSchema,
  FilevineSectionConfigDetailedSchema,
} from '@/services/api/filevineOrganizationService';
import {
  useFilevineOrganizationLinkCreateMutation,
  useFilevineOrganizationLinkGetQuery,
  useFilevineOrganizationsListQuery,
  useFilevineOrganizationUnlinkMutation,
  useFilevineProjectConfigsListQuery,
  useFilevineProjectConfigSyncTriggerMutation,
  useFilevineSectionConfigUpdateTypeMutation,
} from '@/services/api/filevineOrganizationService';
import { useGetMattersQuery } from '@/services/api/mattersService';

interface FilevineOrganizationManagerProps {
  firmId: string;
}

// Available canonical section types - these map to the backend CanonicalSectionType enum
const CANONICAL_SECTION_TYPES = [
  { value: 'medical_providers', label: 'Medical Providers' },
  { value: 'coverage', label: 'Coverage' },
] as const;

type CanonicalSectionType = (typeof CANONICAL_SECTION_TYPES)[number]['value'];

const FilevineOrganizationManagerSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
    <Skeleton className="h-10 w-full max-w-md" />
  </div>
);

const ProjectConfigSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2].map((i) => (
      <div key={i} className="border rounded-lg p-4 bg-white">
        <div className="flex items-center gap-3 mb-3">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-5 w-48" />
        </div>
        <div className="pl-8 space-y-2">
          {[1, 2, 3].map((j) => (
            <div key={j} className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-40" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

interface SectionConfigRowProps {
  section: FilevineSectionConfigDetailedSchema;
  firmId: string;
  onUpdate: () => void;
}

const SectionConfigRow: React.FC<SectionConfigRowProps> = ({
  section,
  firmId,
  onUpdate,
}) => {
  const [updateSectionType, { isLoading: isUpdating }] =
    useFilevineSectionConfigUpdateTypeMutation();

  const handleSectionTypeChange = async (value: string) => {
    const newSectionType =
      value === 'none' ? null : (value as CanonicalSectionType);

    try {
      await updateSectionType({
        firmId,
        id: String(section.id),
        patchedUpdateSectionTypeSchema: {
          section_type: newSectionType ?? undefined,
        },
      }).unwrap();
      toast.success(`Updated section type for "${section.name}"`);
      onUpdate();
    } catch (error) {
      console.error('Error updating section type:', error);
      toast.error('Failed to update section type');
    }
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded">
      <div className="flex items-center gap-2">
        <FolderOpen className="h-4 w-4 text-gray-400" />
        <div>
          <span className="text-sm font-medium text-gray-700">
            {section.name}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            ({section.internal_name})
          </span>
        </div>
      </div>

      <Select
        value={section.section_type ?? 'none'}
        onValueChange={handleSectionTypeChange}
        disabled={isUpdating}
      >
        <SelectTrigger className="w-48 h-8 text-sm">
          <SelectValue placeholder="Select type..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            <span className="text-gray-400">No mapping</span>
          </SelectItem>
          {CANONICAL_SECTION_TYPES.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              {type.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

interface ProjectConfigCardProps {
  project: FilevineProjectConfigWithSectionsSchema;
  firmId: string;
  onUpdate: () => void;
}

const ProjectConfigCard: React.FC<ProjectConfigCardProps> = ({
  project,
  firmId,
  onUpdate,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  // Count how many sections have mappings
  const mappedCount = project.sections.filter((s) => s.section_type).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
            <div className="text-left">
              <div className="font-medium text-gray-900">{project.name}</div>
              <div className="text-sm text-gray-500">
                {project.sections.length} sections
                {mappedCount > 0 && (
                  <span className="text-green-600 ml-2">
                    ({mappedCount} mapped)
                  </span>
                )}
              </div>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t px-4 py-2">
            {project.sections.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">
                No sections in this project type
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {project.sections.map((section) => (
                  <SectionConfigRow
                    key={section.id}
                    section={section}
                    firmId={firmId}
                    onUpdate={onUpdate}
                  />
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const FilevineOrganizationManager: React.FC<
  FilevineOrganizationManagerProps
> = ({ firmId }) => {
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [selectedMatterId, setSelectedMatterId] = useState('');
  const [selectedMatterName, setSelectedMatterName] = useState('');
  const [matterSearch, setMatterSearch] = useState('');
  const debouncedMatterSearch = useDebouncedValue(matterSearch, 300);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  // Fetch current link status
  const {
    data: linkStatus,
    isLoading: isLoadingLink,
    refetch: refetchLink,
  } = useFilevineOrganizationLinkGetQuery({ firmId }, { skip: !firmId });

  // Fetch available organizations
  const { data: organizationsData, isLoading: isLoadingOrgs } =
    useFilevineOrganizationsListQuery({ firmId }, { skip: !firmId });

  // The API returns an array but the type says single object - handle both
  const organizations: FilevineOrganizationWithLinkStatusSchema[] =
    Array.isArray(organizationsData)
      ? organizationsData
      : organizationsData
        ? [organizationsData]
        : [];

  const isLinked = linkStatus?.filevine_organization_id !== null;

  // Fetch project configs with sections only when linked
  const {
    data: projectConfigsData,
    isLoading: isLoadingProjects,
    refetch: refetchProjects,
  } = useFilevineProjectConfigsListQuery(
    { firmId },
    { skip: !firmId || !isLinked },
  );

  const projectConfigs: FilevineProjectConfigWithSectionsSchema[] =
    projectConfigsData ?? [];

  // Fetch matters for sync dialog
  const { data: mattersData, isLoading: isLoadingMatters } = useGetMattersQuery(
    { firm: [firmId], search: debouncedMatterSearch || undefined },
    { skip: !isSyncDialogOpen },
  );
  const matters = useMemo(() => mattersData?.results ?? [], [mattersData]);

  // Mutations
  const [linkOrganization, { isLoading: isLinking }] =
    useFilevineOrganizationLinkCreateMutation();
  const [unlinkOrganization, { isLoading: isUnlinking }] =
    useFilevineOrganizationUnlinkMutation();
  const [syncProjectConfig, { isLoading: isSyncing }] =
    useFilevineProjectConfigSyncTriggerMutation();

  const handleSync = async () => {
    if (!selectedMatterId) {
      toast.error('Please select a matter');
      return;
    }

    try {
      const result = await syncProjectConfig({
        firmId,
        triggerProjectConfigSyncSchema: { matter_id: selectedMatterId },
      }).unwrap();
      toast.success(
        `Synced ${result.project_configs_upserted} project type(s), ${result.section_configs_upserted} section(s), and ${result.field_configs_upserted} field(s)`,
      );
      setIsSyncDialogOpen(false);
      setSelectedMatterId('');
      setSelectedMatterName('');
      setMatterSearch('');
      refetchProjects();
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'data' in error
          ? String((error as { data: { error: string } }).data?.error)
          : 'Failed to sync project config';
      toast.error(message);
    }
  };

  const handleLink = async () => {
    if (!selectedOrgId) {
      toast.error('Please select an organization');
      return;
    }

    try {
      await linkOrganization({
        firmId,
        linkFirmToOrganizationSchema: {
          filevine_organization_id: selectedOrgId,
        },
      }).unwrap();
      toast.success('Successfully linked to Filevine organization');
      setSelectedOrgId(null);
      refetchLink();
    } catch (error) {
      console.error('Error linking organization:', error);
      toast.error('Failed to link organization');
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkOrganization({ firmId }).unwrap();
      toast.success('Successfully unlinked from Filevine organization');
      setIsUnlinkDialogOpen(false);
      refetchLink();
    } catch (error) {
      console.error('Error unlinking organization:', error);
      toast.error('Failed to unlink organization');
    }
  };

  if (isLoadingLink || isLoadingOrgs) {
    return <FilevineOrganizationManagerSkeleton />;
  }

  return (
    <div className="space-y-8">
      {/* Organization Link Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium mb-2">Filevine Organization</h3>
          <p className="text-sm text-gray-500">
            Link this firm to a Filevine organization to enable data
            synchronization.
          </p>
        </div>

        {/* Current Status */}
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${isLinked ? 'bg-green-100' : 'bg-gray-100'}`}
              >
                <Building2
                  className={`h-6 w-6 ${isLinked ? 'text-green-600' : 'text-gray-400'}`}
                />
              </div>
              <div>
                {isLinked ? (
                  <>
                    <div className="font-medium text-gray-900">
                      {linkStatus?.filevine_org_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      Filevine ID: {linkStatus?.filevine_id}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium text-gray-900">Not linked</div>
                    <div className="text-sm text-gray-500">
                      No Filevine organization is currently linked
                    </div>
                  </>
                )}
              </div>
            </div>

            {isLinked && (
              <Button
                variant="outline"
                onClick={() => setIsUnlinkDialogOpen(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Unlink className="h-4 w-4 mr-2" />
                Unlink
              </Button>
            )}
          </div>
        </div>

        {/* Link Section (only show when not linked) */}
        {!isLinked && (
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium mb-3">Link to Organization</h4>
            <div className="flex gap-3">
              <Select
                value={selectedOrgId ?? ''}
                onValueChange={(value) => setSelectedOrgId(value)}
              >
                <SelectTrigger className="w-full max-w-md bg-white">
                  <SelectValue placeholder="Select a Filevine organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem
                      key={org.id}
                      value={String(org.id)}
                      disabled={org.linked_firm_id !== null}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>{org.org_name}</span>
                        {org.linked_firm_id && (
                          <span className="text-xs text-gray-400 ml-2">
                            (linked to {org.linked_firm_name})
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                  {organizations.length === 0 && (
                    <SelectItem value="" disabled>
                      No organizations available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Button
                onClick={handleLink}
                disabled={!selectedOrgId || isLinking}
              >
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link className="h-4 w-4 mr-2" />
                {isLinking ? 'Linking...' : 'Link'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Project Configuration (only show when linked) */}
      {isLinked && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium mb-2">
                Project Types & Section Mapping
              </h3>
              <p className="text-sm text-gray-500">
                Map Filevine sections to canonical types to enable automatic
                data synchronization. Each project type can have one section
                mapped per canonical type.
              </p>
            </div>
            <Button variant="outline" onClick={() => setIsSyncDialogOpen(true)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Project Config
            </Button>
          </div>

          {isLoadingProjects ? (
            <ProjectConfigSkeleton />
          ) : projectConfigs.length === 0 ? (
            <div className="border rounded-lg p-6 bg-gray-50 text-center">
              <FolderOpen className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">
                No project types found for this organization.
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Select a matter with a Filevine project URL to sync its project
                configuration.
              </p>
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => setIsSyncDialogOpen(true)}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Project Config
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {projectConfigs.map((project) => (
                <ProjectConfigCard
                  key={project.id}
                  project={project}
                  firmId={firmId}
                  onUpdate={() => refetchProjects()}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unlink Confirmation Dialog */}
      <Dialog open={isUnlinkDialogOpen} onOpenChange={setIsUnlinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlink Filevine Organization</DialogTitle>
            <DialogDescription>
              Are you sure you want to unlink this firm from{' '}
              <strong>{linkStatus?.filevine_org_name}</strong>? This will stop
              data synchronization between this firm and the Filevine
              organization.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsUnlinkDialogOpen(false)}
              disabled={isUnlinking}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnlink}
              disabled={isUnlinking}
            >
              {isUnlinking ? 'Unlinking...' : 'Unlink'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Project Config Dialog */}
      <Dialog
        open={isSyncDialogOpen}
        onOpenChange={(open) => {
          if (!isSyncing) {
            setIsSyncDialogOpen(open);
            if (!open) {
              setSelectedMatterId('');
              setSelectedMatterName('');
              setMatterSearch('');
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sync Project Configuration</DialogTitle>
            <DialogDescription>
              Select a matter that has a Filevine project URL. The project type
              configuration (sections and fields) will be synced from Filevine.
            </DialogDescription>
          </DialogHeader>
          <Command className="border rounded-md" shouldFilter={false}>
            <CommandInput
              placeholder="Search for a matter..."
              value={matterSearch}
              onValueChange={setMatterSearch}
            />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>
                {isLoadingMatters ? 'Searching...' : 'No matters found.'}
              </CommandEmpty>
              {matters.map((matter) => (
                <CommandItem
                  key={matter.id}
                  value={matter.id}
                  onSelect={() => {
                    setSelectedMatterId(matter.id);
                    setSelectedMatterName(
                      matter.name ?? `Matter ${String(matter.id).slice(0, 8)}`,
                    );
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selectedMatterId === matter.id
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  <div>
                    <div className="text-sm">
                      {matter.name ?? `Matter ${String(matter.id).slice(0, 8)}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {String(matter.id).slice(0, 8)}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
          {selectedMatterId && (
            <p className="mt-2 text-sm text-muted-foreground">
              Selected:{' '}
              <span className="font-medium">{selectedMatterName}</span>
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsSyncDialogOpen(false);
                setSelectedMatterId('');
                setSelectedMatterName('');
                setMatterSearch('');
              }}
              disabled={isSyncing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSync}
              disabled={!selectedMatterId || isSyncing}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
